import {Injectable, inject} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {catchError, map, mergeMap, of, switchMap, withLatestFrom} from 'rxjs';
import {DeviceGroupActions} from './device-group.actions';
import {selectDeviceGroupFilter} from './device-group.selectors';
import { DeviceGroupService, DEVICE_GROUP_STATE_SERVICE_INJECTION_TOKEN } from '../../../../core/services/api/device-group-state-service.interface';

@Injectable()
export class DeviceGroupEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private deviceGroupService = inject<DeviceGroupService>(DEVICE_GROUP_STATE_SERVICE_INJECTION_TOKEN);

  public updateFilter$ = createEffect(() => this.actions$.pipe(
    ofType(DeviceGroupActions.public.updateDeviceGroupFilter),
    map(() => DeviceGroupActions.private.loadDeviceGroupsWithFilters()),
  ));

  public loadDeviceGroups$ = createEffect(() => this.actions$.pipe(
    ofType(DeviceGroupActions.private.loadDeviceGroupsWithFilters),
    withLatestFrom(this.store.select(selectDeviceGroupFilter)),
    mergeMap(([_action, filter]) => this.deviceGroupService.getDeviceGroups(filter).pipe(
      map((responseDto) => DeviceGroupActions.private.loadDeviceGroupsSuccess({deviceGroupsResponseDto: responseDto})),
      catchError((error) => of(DeviceGroupActions.private.loadDeviceGroupsFailure({error}))),
    )),
  ));

  public loadDeviceGroupsWithProvidedFilters$ = createEffect(() => this.actions$.pipe(
    ofType(DeviceGroupActions.private.loadDeviceGroupsWithProvidedFilters),
    mergeMap((props) => this.deviceGroupService.getDeviceGroups(props.filter).pipe(
      map((responseDto) => DeviceGroupActions.private.loadDeviceGroupsWithProvidedFiltersSuccess({
        deviceGroupsResponseDto: responseDto,
      })),
      catchError((error) => of(DeviceGroupActions.private.loadDeviceGroupsWithProvidedFiltersFailure({error}))),
    )),
  ));

  public loadAllDeviceGroupsByFilters$ = createEffect(() => this.actions$.pipe(
    ofType(DeviceGroupActions.public.loadAllDeviceGroupsByFilters),
    map((props) => DeviceGroupActions.private.loadNextOfAll({page: 0, filter: props.filter ?? {}})),
    catchError((error) => of(DeviceGroupActions.private.loadAllDeviceGroupsByFiltersFailure({error}))),
  ));

  public loadNextOfAll$ = createEffect(() => this.actions$.pipe(
    ofType(DeviceGroupActions.private.loadNextOfAll),
    switchMap((props) => {
      const pageSize = 50;
      return this.deviceGroupService.getDeviceGroups({...props.filter, pageSize, page: props.page}).pipe(
        map((responseDto) => {
          this.store.dispatch(DeviceGroupActions.private.loadNextOfAllSuccess({
            deviceGroupsResponseDto: responseDto,
          }));

          if ((props.page + 1) * pageSize >= responseDto.totalCount) {
            return DeviceGroupActions.private.loadAllDeviceGroupsByFiltersSuccess();
          }

          return DeviceGroupActions.private.loadNextOfAll({
            page: props.page + 1,
            filter: props.filter,
          });
        }),
        catchError((error) => of(
          DeviceGroupActions.private.loadNextOfAllFailure({error}),
          DeviceGroupActions.private.loadAllDeviceGroupsByFiltersFailure({error}),
        )),
      );
    }),
  ));

  public triggerDeviceGroupAction$ = createEffect(() => this.actions$.pipe(
    ofType(DeviceGroupActions.public.triggerDeviceGroupAction),
    switchMap((props) => this.deviceGroupService.triggerDeviceGroupAction({
      id: props.id,
      value: props.value,
    }).pipe(
      map((responseDto) => DeviceGroupActions.private.triggerDeviceGroupActionSuccess({responseDto})),
      catchError((error) => of(DeviceGroupActions.private.triggerDeviceGroupActionFailure({id: props.id, error}))),
    )),
  ));

  public loadDeviceGroupById$ = createEffect(() => this.actions$.pipe(
    ofType(DeviceGroupActions.public.loadDeviceGroupById),
    switchMap((props) => this.deviceGroupService.getDeviceGroupById({id: props.id}).pipe(
      map((responseDto) => DeviceGroupActions.private.loadDeviceGroupByIdSuccess({responseDto})),
      catchError((error) => of(DeviceGroupActions.private.loadDeviceGroupByIdFailure({id: props.id, error}))),
    )),
  ));

  public createDeviceGroup$ = createEffect(() => this.actions$.pipe(
    ofType(DeviceGroupActions.public.createDeviceGroup),
    switchMap((props) => this.deviceGroupService.createDeviceGroup(props.requestDto).pipe(
      map((responseDto) => DeviceGroupActions.private.createDeviceGroupSuccess({responseDto})),
      catchError((error) => of(DeviceGroupActions.private.createDeviceGroupFailure({error}))),
    )),
  ));

  public updateDeviceGroup$ = createEffect(() => this.actions$.pipe(
    ofType(DeviceGroupActions.public.updateDeviceGroup),
    switchMap((props) => this.deviceGroupService.updateDeviceGroup({
      id: props.id,
      requestDto: props.requestDto,
    }).pipe(
      map((responseDto) => DeviceGroupActions.private.updateDeviceGroupSuccess({responseDto})),
      catchError((error) => of(DeviceGroupActions.private.updateDeviceGroupFailure({id: props.id, error}))),
    )),
  ));

  public deleteDeviceGroupById$ = createEffect(() => this.actions$.pipe(
    ofType(DeviceGroupActions.public.deleteDeviceGroupById),
    switchMap((props) => this.deviceGroupService.deleteDeviceGroup({id: props.id}).pipe(
      map(() => DeviceGroupActions.private.deleteDeviceGroupSuccess({id: props.id})),
      catchError((error) => of(DeviceGroupActions.private.deleteDeviceGroupFailure({id: props.id, error}))),
    )),
  ));

  public refreshAfterMutation$ = createEffect(() => this.actions$.pipe(
    ofType(
      DeviceGroupActions.private.createDeviceGroupSuccess,
      DeviceGroupActions.private.updateDeviceGroupSuccess,
      DeviceGroupActions.private.deleteDeviceGroupSuccess,
    ),
    map(() => DeviceGroupActions.private.loadDeviceGroupsWithFilters()),
  ));
}
